import z from "zod";
import { OrganizationUserInfoSchema } from "../organization/organization.types";

export const AuthSchema = z.object({
	id: z.number(),
	name: z.string(),
	email: z.string().email(),
	password: z.string(),
});

// UserStatus schema matching swagger specification
export const UserStatusSchema = z.object({
	code: z.number(),
	name: z.string(),
	description: z.string(),
});

/**
 * Platform role data structure from API
 * Note: API returns "platformRoles" not "systemRoles"
 */
export const PlatformRoleSchema = z.object({
	code: z.number(),
	name: z.string(),
	description: z.string(),
});

// UserInfo schema to match swagger /authentication/user-info exactly
export const UserInfoSchema = z.object({
	pendingInvite: z.boolean(),
	currentOrganizationId: z.string().uuid().nullable(),
	lastLoggedInAt: z.string().nullable(),
	lastLoggedInAtOffset: z.string().nullable(),
	updatedAt: z.string().nullable(),
	updatedAtOffset: z.string().nullable(),
	createdAt: z.string().nullable(),
	createdAtOffset: z.string().nullable(),
	status: UserStatusSchema,
	platformRoles: z.array(PlatformRoleSchema),
	platformRole: PlatformRoleSchema,
	organizationUserInfo: z.array(OrganizationUserInfoSchema),
	belongsTo: z.string().nullable(),
	organizationRoles: z.string().nullable(),
	organizations: z.string().nullable(),
	id: z.string().uuid(),
	hasAvatar: z.boolean().optional(),
	displayName: z.string(),
	email: z.string().email(),
	firstName: z.string(),
	middleName: z.string().nullable(),
	lastName: z.string(),
	usingFirstPartyAuth: z.boolean(),
	mfaPhone: z.string().nullable(),
});

export type Auth = z.infer<typeof AuthSchema>;
export type UserStatus = z.infer<typeof UserStatusSchema>;
export type UserInfo = z.infer<typeof UserInfoSchema>;

// Exchange request schema for POST /authentication/exchange
export const ExchangeRequestSchema = z.object({
	exchangeToken: z.string().nullable(),
});

export type ExchangeRequest = z.infer<typeof ExchangeRequestSchema>;

/**
 * Set initial password request for PUT /authentication/{userId}/initial-password
 * Used when a user accepts an invite and sets their password (token from email link).
 */
export const SetInitialPasswordRequestSchema = z.object({
	password: z.string(),
	token: z.string(),
});

export type SetInitialPasswordRequest = z.infer<
	typeof SetInitialPasswordRequestSchema
>;

export const ApiErrorSchema = z.object({
	message: z.string(),
	code: z.string().optional(),
	details: z.record(z.string(), z.unknown()).optional(),
});

export type ApiError = z.infer<typeof ApiErrorSchema>;

export class AuthApiError extends Error {
	public code?: string;
	public details?: Record<string, unknown>;

	constructor(
		message: string,
		code?: string,
		details?: Record<string, unknown>,
	) {
		super(message);
		this.name = "AuthApiError";
		this.code = code;
		this.details = details;

		// Maintains proper stack trace for where our error was thrown (only available on V8)
		if ("captureStackTrace" in Error) {
			(
				Error as {
					captureStackTrace?: (
						target: object,
						constructorOpt: typeof AuthApiError,
					) => void;
				}
			).captureStackTrace?.(this, AuthApiError);
		}
	}
}

// Specific error class for getUserInfo operations
export class UserInfoError extends AuthApiError {
	public statusCode?: number;

	constructor(
		message: string,
		code: string,
		details?: Record<string, unknown>,
		statusCode?: number,
	) {
		super(message, code, details);
		this.name = "UserInfoError";
		this.statusCode = statusCode;

		// Maintains proper stack trace for where our error was thrown (only available on V8)
		if ("captureStackTrace" in Error) {
			(
				Error as {
					captureStackTrace?: (
						target: object,
						constructorOpt: typeof UserInfoError,
					) => void;
				}
			).captureStackTrace?.(this, UserInfoError);
		}
	}
}

// =============================================================================
// PERMISSIONS API TYPES - Based on Swagger Specification
// =============================================================================

/**
 * Action to check for permission validation
 * Based on /valid-actions endpoint schema
 */
export const ActionToCheckSchema = z.object({
	name: z.string(), // Required action name
	entityType: z.string().nullable().optional(), // Optional entity type
	entityId: z.string().nullable().optional(), // Optional specific entity ID
	subOrganizationId: z.string().nullable().optional(),
});

export type ActionToCheck = z.infer<typeof ActionToCheckSchema>;

/**
 * Result of action validation
 * Response from /valid-actions endpoint
 */
export const ActionValidationResultSchema = z.object({
	validForCurrentUser: z.boolean(),
	name: z.string(),
	entityType: z.string().nullable(),
	entityId: z.string().nullable(),
});

export type ActionValidationResult = z.infer<
	typeof ActionValidationResultSchema
>;

/**
 * Bulk action validation request
 */
export const ValidActionsRequestSchema = z.array(ActionToCheckSchema);
export type ValidActionsRequest = z.infer<typeof ValidActionsRequestSchema>;

/**
 * Bulk action validation response
 */
export const ValidActionsResponseSchema = z.array(ActionValidationResultSchema);
export type ValidActionsResponse = z.infer<typeof ValidActionsResponseSchema>;

/**
 * Organization status data structure from API
 */
export const OrganizationStatusSchema = z.object({
	code: z.number(),
	name: z.string(),
	description: z.string(),
});

/**
 * Simple organization data structure from API
 * Note: API only returns id and name, not the enhanced fields
 */
export const SimpleOrganizationSchema = z.object({
	id: z.string().uuid(),
	name: z.string(),
});

/**
 * Enhanced organization data structure from API (LEGACY - NOT USED BY PERMISSIONS ENDPOINT)
 * Note: This schema is kept for backward compatibility but is NOT what the permissions endpoint returns
 */
export const EnhancedOrganizationSchema = z.object({
	name: z.string(),
	statusCode: z.number(),
	status: OrganizationStatusSchema,
	active: z.boolean(),
	id: z.string().uuid(),
	createdBy: z.string().uuid().nullable(),
	createdAt: z.string().datetime().nullable(),
	createdAtOffset: z.string(),
	updatedBy: z.string().uuid().nullable(),
	updatedAt: z.string().datetime().nullable(),
	updatedAtOffset: z.string(),
});

/**
 * Organization role permission data structure from API
 */
export const OrganizationRolePermissionSchema = z.object({
	// This appears to be empty in the example, but we'll define a flexible structure
	id: z.string().uuid().optional(),
	name: z.string().optional(),
	description: z.string().optional(),
});

/**
 * Simple organization role data structure from API
 * Note: API only returns name field for organization roles
 */
export const SimpleOrganizationRoleSchema = z.object({
	name: z.string(),
});

/**
 * Organization role data structure from API (LEGACY - NOT USED BY PERMISSIONS ENDPOINT)
 * Note: This schema is kept for backward compatibility but is NOT what the permissions endpoint returns
 */
export const OrganizationRoleSchema = z.object({
	organizationId: z.string().uuid(),
	name: z.string(),
	organizationRolePermissions: z.array(OrganizationRolePermissionSchema),
	id: z.string().uuid(),
	createdBy: z.string().uuid().nullable().optional(),
	createdAt: z.string().datetime().nullable().optional(),
	createdAtOffset: z.string().nullable().optional(),
	updatedBy: z.string().uuid().nullable().optional(),
	updatedAt: z.string().datetime().nullable().optional(),
	updatedAtOffset: z.string().nullable().optional(),
});

/**
 * Permission data structure from API (keeping from previous structure)
 */
export const PermissionSchema = z.object({
	name: z.string(),
	description: z.string(),
	id: z.string().uuid(),
	createdBy: z.string().uuid().nullable().optional(),
	createdAt: z.string().datetime().nullable().optional(),
	createdAtOffset: z.string().nullable().optional(),
	updatedBy: z.string().uuid().nullable().optional(),
	updatedAt: z.string().datetime().nullable().optional(),
	updatedAtOffset: z.string().nullable().optional(),
});

/**
 * Organization and permissions data structure from API
 * Note: Uses simplified schemas that match actual API response
 */
export const OrganizationAndPermissionsSchema = z.object({
	organization: SimpleOrganizationSchema,
	permissions: z.array(PermissionSchema),
	organizationRoles: z.array(SimpleOrganizationRoleSchema),
});

/**
 * Complete user permissions response from /authentication/user-info/permissions
 * and /users/{userId}/permissions endpoints
 *
 * Note: The API returns platformRoles (not systemRoles) and simplified organization data.
 * Verified via Chrome DevTools on 2025-10-23.
 */
export const UserPermissionsResponseSchema = z.object({
	platformRoles: z.array(PlatformRoleSchema),
	organizationsAndPermissions: z.array(OrganizationAndPermissionsSchema),
});

export type PlatformRole = z.infer<typeof PlatformRoleSchema>;
export type SimpleOrganization = z.infer<typeof SimpleOrganizationSchema>;
export type SimpleOrganizationRole = z.infer<
	typeof SimpleOrganizationRoleSchema
>;
export type OrganizationStatus = z.infer<typeof OrganizationStatusSchema>;
export type EnhancedOrganization = z.infer<typeof EnhancedOrganizationSchema>;
export type OrganizationRolePermission = z.infer<
	typeof OrganizationRolePermissionSchema
>;
export type OrganizationRole = z.infer<typeof OrganizationRoleSchema>;
export type Permission = z.infer<typeof PermissionSchema>;
export type OrganizationAndPermissions = z.infer<
	typeof OrganizationAndPermissionsSchema
>;
export type UserPermissionsResponse = z.infer<
	typeof UserPermissionsResponseSchema
>;

/**
 * Flattened permissions structure for internal use
 */
export interface UserPermissions {
	permissions: Array<{
		id: string;
		name: string;
		description: string;
		organizationId: string;
		organizationName: string;
		isRole?: boolean; // Flag to distinguish organization roles from permissions
	}>;
	organizations: Array<{
		id: string;
		name: string;
		createdAt?: string | null;
		updatedAt?: string | null;
		status?: OrganizationStatus;
		statusCode?: number;
		active?: boolean;
	}>;
	platformRoles: Array<{
		id: string;
		name: string;
		description: string;
		code: number;
	}>;
}

/**
 * Permissions-specific error class
 */
export class PermissionsError extends AuthApiError {
	public statusCode?: number;
	public userId?: string;
	public entityType?: string;
	public entityId?: string;

	constructor(
		message: string,
		code: string,
		details?: Record<string, unknown> & {
			statusCode?: number;
			userId?: string;
			entityType?: string;
			entityId?: string;
		},
		statusCode?: number,
	) {
		super(message, code, details);
		this.name = "PermissionsError";
		this.statusCode = statusCode || details?.statusCode;
		this.userId = details?.userId;
		this.entityType = details?.entityType;
		this.entityId = details?.entityId;

		// Maintains proper stack trace for where our error was thrown (only available on V8)
		if ("captureStackTrace" in Error) {
			(
				Error as {
					captureStackTrace?: (
						target: object,
						constructorOpt: typeof PermissionsError,
					) => void;
				}
			).captureStackTrace?.(this, PermissionsError);
		}
	}
}
